/* eslint-disable no-unused-vars */
import Vue from "vue";
import moment from "moment";
import {
  getAuth,
  updateProfile,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
} from "firebase/auth";
import { getDatabase, ref, get, set, update, onValue, onDisconnect, serverTimestamp, remove } from "firebase/database";
import { getFunctions, httpsCallable } from "firebase/functions";

import {
  LOGIN,
  RESET_PASSWORD,
  LOGOUT,
  REGISTER,
  GET_AUTH,
  GET_AUTH_TEMPORARY,
  UPDATE_AUTH,
  UPDATE_AUTH_SEEN,
  UPDATE_AUTH_STATE,
  UPDATE_AUTH_WALLET,
  UPDATE_AUTH_ONBOARDING,
  FETCH_AUTH_NFTS,
  CONNECT_TO_METAMASK,
  MINT_GLADIATEUR,
  FETCH_MOUTRUGEONS,
  FETCH_NFTS_OWNER,
  BAN_USER,
} from "../actions";
import { SET_AUTH, SET_WALLET, SET_ERROR, PURGE_AUTH } from "../mutations";
import { putTokenToStorage } from "@/helpers/auth";
import { encodeAsFirebaseKey, decodeFromFirebaseKey } from "@/helpers/utils";
import { holdsToken, connectToMetamask, getOwnerOf } from "@/helpers/web3";
import { CHAIN_NAMES_TO_IDS, CONTRACT_ADDRESS_NFTS, CONTRACT_ADDRESS_MOUTRUGEON } from "@/constants/web3";
import { DEFAULT_CHANNELS } from "@/constants/members";

const database = getDatabase();
const functions = getFunctions();

const state = {
  user: {},
  wallet: {},

  isAuthenticated: false,
  errors: null,
};

const getters = {
  currentUser(state) {
    return state.user || {};
  },
  currentWallet(state) {
    return state.wallet || {};
  },

  isAuthenticated(state) {
    return state.isAuthenticated;
  },
  isBanned(state) {
    return state.isBanned;
  },
  hasErrors(state) {
    return state.errors;
  },
};

const mutations = {
  [SET_AUTH](state, payload) {
    state.isAuthenticated = true;
    for (const key in payload) Vue.set(state.user, key, payload[key]);

    state.errors = {};
  },
  [SET_WALLET](state, payload) {
    for (const key in payload) Vue.set(state.wallet, key, payload[key]);

    state.errors = {};
  },

  [PURGE_AUTH]({ isAuthenticated, user, errors }) {
    isAuthenticated = false;
    user = {};
    errors = {};
  },

  [SET_ERROR]({ errors }, error) {
    errors = error;
  },
};

const actions = {
  async [LOGIN]({ commit }, { email, password }) {
    const auth = getAuth();

    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password);

      const token = await user.getIdToken(true);
      putTokenToStorage(token);

      commit(SET_AUTH, user);
    } catch (error) {
      console.log(error);
      return error;
    }
  },

  async [LOGOUT]({ commit }) {
    const auth = getAuth();

    try {
      await signOut(auth);

      commit(PURGE_AUTH);
    } catch (error) {
      console.log(error);
    }
  },
  async [RESET_PASSWORD](_, { email }) {
    const auth = getAuth();
    let result = "success";

    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.log(error);
      result = "fail";
    }

    return result;
  },
  async [REGISTER]({ commit }, { firstName, email, password, ...others }) {
    const auth = getAuth();

    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, password);

      await updateProfile(user, { displayName: firstName });

      const token = await user.getIdToken(true);
      putTokenToStorage(token);

      commit(SET_AUTH, user);

      const { uid } = user;
      const createdAt = moment().toISOString();

      set(ref(database, `members/${uid}`), {
        private: { email, firstName, isOnboardingDone: false, ...others },
        public: { createdAt, displayName: firstName },
      });

      const emailKey = encodeAsFirebaseKey(email);
      set(ref(database, `membersPublic/${emailKey}`), { createdAt });

      for await (const c of DEFAULT_CHANNELS) {
        const { id, createdAt = "", name, description = "", cover = "", thumbnail = "" } = c;

        await update(ref(database, `members/${uid}/channels/${id}`), {
          addedOn: createdAt,
          role: "member",
          config: {
            addedOn: createdAt,
            name,
            description,
            cover,
            thumbnail,
          },
        });

        await update(ref(database, `channels/${id}/members/${uid}`), {
          role: "member",
          addedOn: createdAt,
        });
      }
    } catch (error) {
      console.log(error);
      return error;
    }
  },
  async [GET_AUTH]({ commit, rootGetters }) {
    const { uid } = rootGetters["auth/currentUser"];
    // Use get to make sure we get the most important data before INIT
    const snapshotPrivate = await get(ref(database, `members/${uid}/private`));
    commit(SET_AUTH, { ...snapshotPrivate.val() });

    const snapshotPublic = await get(ref(database, `members/${uid}/public`));

    commit(SET_AUTH, { ...snapshotPublic.val() });

    // Add listeners
    onValue(ref(database, `members/${uid}/private`), (snapshot) => {
      const data = snapshot.val();
      commit(SET_AUTH, { ...data });
    });

    onValue(ref(database, `members/${uid}/public`), (snapshot) => {
      const data = snapshot.val();
      commit(SET_AUTH, { ...data });
    });

    onValue(ref(database, `members/${uid}/wallet`), (snapshot) => {
      const data = snapshot.val();
      commit(SET_WALLET, { ...data });
    });
  },
  async [GET_AUTH_TEMPORARY](_, payload) {
    const { email } = payload;

    const emailKey = encodeAsFirebaseKey(email);
    const snapshotPublic = await get(ref(database, `membersPublic/${emailKey}`));

    return snapshotPublic.val();
  },
  async [UPDATE_AUTH]({ commit, rootGetters }, payload) {
    const auth = getAuth();

    const { uid } = rootGetters["auth/currentUser"];

    const { private: memberPrivate, public: memberPublic } = payload;

    const updates = {};

    if (memberPrivate) {
      for (const key in memberPrivate) {
        updates[`members/${uid}/private/${key}`] = memberPrivate[key];
      }

      commit(SET_AUTH, { ...memberPrivate });
    }

    if (memberPublic) {
      for (const key in memberPublic) {
        updates[`members/${uid}/public/${key}`] = memberPublic[key];
        updates[`membersOnline/${uid}/${key}`] = memberPublic[key];
      }

      const { photoURL, displayName } = memberPublic;
      if (photoURL) updateProfile(auth.currentUser, { photoURL });
      if (displayName) updateProfile(auth.currentUser, { displayName });

      commit(SET_AUTH, { ...memberPublic });
    }

    update(ref(database), updates);
  },
  async [UPDATE_AUTH_STATE]({ commit, rootGetters }) {
    const { uid, displayName, photoURL = null } = rootGetters["auth/currentUser"];

    const connectedRef = ref(database, ".info/connected");
    const memberOnlineRef = ref(database, `membersOnline/${uid}`);
    const memberPublicStateRef = ref(database, `members/${uid}/public/state`);
    const memberPublicLastSeenRef = ref(database, `members/${uid}/public/lastSeenOn`);

    onValue(connectedRef, (snap) => {
      if (snap.val() === true) {
        set(memberOnlineRef, { state: "online", photoURL, displayName });
        set(memberPublicStateRef, "online");

        onDisconnect(memberPublicLastSeenRef).set(serverTimestamp());
        onDisconnect(memberOnlineRef).remove();
      }
    });
  },
  async [UPDATE_AUTH_SEEN]({ commit, rootGetters }) {
    const { uid } = rootGetters["auth/currentUser"];

    const updates = {};
    updates[`members/${uid}/public/lastSeenOn`] = serverTimestamp();
    updates[`membersOnline/${uid}/lastSeenOn`] = serverTimestamp();

    update(ref(database), updates);
  },
  async [UPDATE_AUTH_WALLET]({ rootGetters, commit }, { walletId, ...payload }) {
    const { uid } = rootGetters["auth/currentUser"];
    const { chainId, isMetamaskUnlocked } = payload;

    if (!isMetamaskUnlocked) commit(SET_WALLET, { errorMetamask: true });
    else commit(SET_WALLET, { errorMetamask: false });

    if (chainId === CHAIN_NAMES_TO_IDS["polygon"]) commit(SET_WALLET, { errorChain: false });
    else commit(SET_WALLET, { errorChain: true });

    const updates = {};

    if (walletId) {
      const snapshot = await get(ref(database, `members/${uid}/wallet/walletId`));
      const existingWallet = snapshot.val();

      // We always make sure the original wallet is linked to the email to avoid tracking issues.
      // If there is already a wallet, we can't override it and need to show an error for the user to switch.
      if (!existingWallet) {
        updates[`members/${uid}/wallet/walletId`] = walletId;
        commit(SET_WALLET, { errorWallet: false });
      } else if (walletId == existingWallet) {
        commit(SET_WALLET, { errorWallet: false });
      } else {
        commit(SET_WALLET, { errorWallet: true });
      }
    }
    for (const key in payload) {
      updates[`members/${uid}/wallet/${key}`] = payload[key];
    }

    update(ref(database), updates);
  },
  [UPDATE_AUTH_ONBOARDING]({ rootGetters }, { isDone, ...payload }) {
    const { uid } = rootGetters["auth/currentUser"];

    const updates = {};
    updates[`members/${uid}/private/isOnboardingDone`] = isDone;
    updates[`members/${uid}/private/onboarding`] = payload;

    update(ref(database), updates);
  },

  async [FETCH_AUTH_NFTS]({ state }) {
    const { walletId } = state.wallet;
    console.log("WalletId", walletId);
    if (!walletId) return [];

    const items = [];
    for await (const nft of Object.entries(CONTRACT_ADDRESS_NFTS)) {
      // eslint-disable-next-line no-unused-vars
      const [id, { address, abi, metadata }] = nft;

      console.log("id", id);
      const amountOfNFTsOwned = await holdsToken(walletId, abi, address);
      console.log("amountOfNFTsOwned", amountOfNFTsOwned);
      if (amountOfNFTsOwned) items.push({ id, ...metadata, amount: amountOfNFTsOwned });
    }

    return items;
  },

  async [FETCH_NFTS_OWNER]() {
    const { address, abi } = CONTRACT_ADDRESS_MOUTRUGEON;

    const owners = {};
    for (let i = 1; i < 688; i++) {
      console.log("I", i);
      const result = await getOwnerOf(abi, address, i);

      const resultToLowerCase = result.toLowerCase();
      if (!owners[resultToLowerCase]) owners[resultToLowerCase] = [i];
      else owners[resultToLowerCase].push(i);
    }

    const updates = {};
    updates[`moutrugeons/owners`] = owners;

    update(ref(database), updates);

    return owners;
  },

  async [FETCH_MOUTRUGEONS]() {
    const { walletId } = state.wallet;
    console.log(`moutrugeons/owners/${walletId}`);
    const snap = await get(ref(database, `moutrugeons/owners/${walletId}`));
    const result = snap.val();
    console.log("resu", result);
    return result;
  },

  async [CONNECT_TO_METAMASK]({ dispatch }) {
    const { chainId, walletId, isMetamaskUnlocked } = await connectToMetamask();
    dispatch(UPDATE_AUTH_WALLET, { chainId, walletId, isMetamaskUnlocked });
  },

  async [MINT_GLADIATEUR](_, payload) {
    try {
      const mintGladiateur = httpsCallable(functions, "mintGladiateur");
      const { data = {} } = await mintGladiateur(payload);
      console.log("DATA", data);
      const { transactionHash } = data;
      if (transactionHash) data.isSuccess = true;

      return data;
    } catch (e) {
      console.log("ERR", e);
      return { isSuccess: false, error: e };
    }
  },

  async [BAN_USER]({ state }, { senderId }) {
    try {
      update(ref(database, `members/${senderId}/public`), { isBanned: true });
      update(ref(database, `membersOnline/${senderId}/public`), { isBanned: true });
    } catch (error) {
      console.log(senderId);
      console.log(error);
      return error;
    }
  },
};

const plugin = (store) => {
  if (!window.ethereum) return;

  window.ethereum.on("connect", async () => {
    const { initialized } = store._modules.root.state;
    if (!initialized) return;
    console.log("METAMASK EVENT: Connect");
    const { chainId, walletId, isMetamaskUnlocked } = await connectToMetamask();
    store.dispatch(`auth/${UPDATE_AUTH_WALLET}`, { chainId, walletId, isMetamaskUnlocked });
  });

  window.ethereum.on("accountsChanged", async () => {
    const { initialized } = store._modules.root.state;
    if (!initialized) return;
    console.log("METAMASK EVENT: Account Changed");
    const { chainId, walletId, isMetamaskUnlocked } = await connectToMetamask();
    store.dispatch(`auth/${UPDATE_AUTH_WALLET}`, { chainId, walletId, isMetamaskUnlocked });
  });

  window.ethereum.on("chainChanged", async () => {
    const { initialized } = store._modules.root.state;
    if (!initialized) return;
    console.log("METAMASK EVENT: Chain Changed");
    const { chainId, walletId, isMetamaskUnlocked } = await connectToMetamask();
    store.dispatch(`auth/${UPDATE_AUTH_WALLET}`, { chainId, walletId, isMetamaskUnlocked });
  });
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
  plugin,
};
